import { JsonLdResult } from 'vet-bones/utils';

import {
  STORED_QUERY_TAG_IRI,
  SYSTEM_QUERY_CREATOR_IRI,
  SYSTEM_QUERY_DATABASE_IRI,
  SYSTEM_QUERY_NAME_IRI,
  SYSTEM_QUERY_STRING_IRI,
  SYSTEM_QUERY_VOICEBOX_QUESTION_IRI,
  SYSTEM_REASONING_QUERY_IRI,
  SYSTEM_SHARED_QUERY_IRI,
} from 'src/common/constants/uris';
import { StoredQuery } from 'src/common/store/storedQueries/StoredQueriesState';
import { emptyArray } from 'src/common/utils/emptyArray';

const getSingle = <K extends string | number | boolean | undefined>(
  result: JsonLdResult[keyof JsonLdResult],
  key: string
): K | undefined => {
  if (Array.isArray(result)) {
    return getSingle(result[0], key);
  }
  switch (typeof result) {
    case 'object':
      return getSingle(result[key], key);
    default:
      return (result || undefined) as K;
  }
};

const getList = <K extends string | number | boolean | undefined>(
  result: JsonLdResult[keyof JsonLdResult]
): K[] | undefined => {
  if (Array.isArray(result)) {
    return result.map((value) => getSingle<K>(value, '@value')) as K[];
  }
  return undefined;
};

// while this utility is similar to the one in lassie, JSON-LD results are formatted
// differently in Studio (see formatJsonLd in lassie), so we need to parse these results
// a bit differently
const getSingleOrListInArray = <
  K extends string | number | boolean | undefined
>(
  result: JsonLdResult[keyof JsonLdResult]
): K[] => {
  if (Array.isArray(result)) {
    return getList<K>(result);
  }

  const single = getSingle<K>(result, '@value');

  return single ? [single] : (emptyArray as K[]);
};

export const parseStoredQueriesResults = (results: JsonLdResult[]) => {
  return Object.fromEntries(
    results
      .map((result): [string, StoredQuery] | null => {
        const name = getSingle<string>(result[SYSTEM_QUERY_NAME_IRI], '@value');
        if (!name) {
          return null;
        }

        const types = result['@type'] as string[] | undefined;
        const reasoning = types?.includes(SYSTEM_REASONING_QUERY_IRI) || false;
        const shared = types?.includes(SYSTEM_SHARED_QUERY_IRI) || false;

        const creator = getSingle<string>(
          result[SYSTEM_QUERY_CREATOR_IRI],
          '@value'
        );
        const database = getSingle<string>(
          result[SYSTEM_QUERY_DATABASE_IRI],
          '@value'
        );
        const query =
          getSingle<string>(result[SYSTEM_QUERY_STRING_IRI], '@value') || '';

        const userTags = new Set(
          getSingleOrListInArray<string>(result[STORED_QUERY_TAG_IRI])
        );
        const voiceboxQuestions = getSingleOrListInArray<string>(
          result[SYSTEM_QUERY_VOICEBOX_QUESTION_IRI]
        );

        return [
          name,
          {
            creator,
            database,
            name,
            query,
            reasoning,
            shared,
            tags: {
              userTags,
              voiceboxQuestions,
            },
          },
        ];
      })
      .filter((q): q is [string, StoredQuery] => Boolean(q))
  );
};
